/* istanbul ignore file */
/* eslint-disable max-len */
import React, { useState } from 'react';
import { Modal } from 'react-bootstrap';
import ErrorBanner from '@avtechsoftware/react-component-library/src/components/common/ErrorBanner';
import PropTypes from 'prop-types';
import DateRangeSelector, {
  calculateStartAndEndTime,
} from '@avtechsoftware/react-component-library/src/components/common/DateRangeSelector/DateRangeSelector';
import { ReportRangeTypes } from '@avtechsoftware/ra-javascript-utilities/src/types/ReportRangeTypes';
import AvtechApplicationCodes from '@avtechsoftware/ra-javascript-utilities/src/types/AvtechApplicationCodes';
import axios from 'axios';
import ModalComponent from '../../../layout/Modals/ModalComponent/ModalComponent';
import { connectModalContainerContextToComponent } from '../../../../contexts/ModalContext/ModalContext';
import envConfig from '../../../../envConfig';

function DownloadDataModal({ setModalJSX, deviceId }) {
  const [show, setShow] = useState(true);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Initialize with default range type and calculate initial times
  const defaultRangeType = ReportRangeTypes.TWENTY_FOUR_HOURS;
  // eslint-disable-next-line max-len
  const { startTime: initialStartTime, endTime: initialEndTime } = calculateStartAndEndTime(defaultRangeType);

  const [dateRangeData, setDateRangeData] = useState({
    rangeTypeId: defaultRangeType,
    startTime: initialStartTime,
    endTime: initialEndTime,
  });

  const closeModalHandler = () => {
    setShow(false);
    setModalJSX((<></>));
  };

  const updateDateRangeData = (updatedDateRangeData) => {
    setDateRangeData(updatedDateRangeData);
  };

  const toggleRangeTypeMode = () => {
    const newRangeTypeId = dateRangeData.rangeTypeId === ReportRangeTypes.CUSTOM_RANGE
      ? ReportRangeTypes.TWENTY_FOUR_HOURS
      : ReportRangeTypes.CUSTOM_RANGE;

    const { startTime, endTime } = calculateStartAndEndTime(newRangeTypeId);
    setDateRangeData({
      rangeTypeId: newRangeTypeId,
      startTime,
      endTime,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      const { startTime, endTime } = dateRangeData;

      const response = await axios({
        url: `${envConfig.raaUrl}/devices/data/download-device-data-by-date-range/${deviceId}`,
        method: 'GET',
        params: {
          startTime: startTime.toISOString(),
          endTime: endTime.toISOString(),
        },
        responseType: 'blob',
      });
      // Create a blob link for downloading
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      // Set filename from headers or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'device-data.csv';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch && filenameMatch.length === 2) {
          // eslint-disable-next-line prefer-destructuring
          filename = filenameMatch[1];
        }
      }
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      // Cleanup
      window.URL.revokeObjectURL(url);
      link.remove();
      closeModalHandler();
    } catch (err) {
      setError('Unexpected error downloading data. Please try again.');
      // eslint-disable-next-line no-console
      console.error('Download error:', err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalComponent
      showConfirmCallToAction
      show={show}
      close={closeModalHandler}
      confirmButtonText="Download Data"
      confirmButtonAction={handleSubmit}
      isBtnDisabled={submitting}
    >
      <Modal.Header closeButton>
        <Modal.Title>Download Data</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="px-3">
          {error && (
            <div className="row">
              <div className="col">
                <ErrorBanner onCloseError={() => setError('')}>
                  {error}
                </ErrorBanner>
              </div>
            </div>
          )}
          <div className="form-group">
            <p>Download sensor data from the following time period:</p>
            <div className="d-flex justify-content-center">
              <DateRangeSelector
                updateDateRangeData={updateDateRangeData}
                reportRangeTypeId={dateRangeData.rangeTypeId}
                startTime={dateRangeData.startTime}
                endTime={dateRangeData.endTime}
                toggleRangeTypeMode={toggleRangeTypeMode}
                applicationCode={AvtechApplicationCodes.RAA}
                timeZone="UTC"
              />
            </div>
          </div>
        </div>
      </Modal.Body>
    </ModalComponent>
  );
}

DownloadDataModal.propTypes = {
  setModalJSX: PropTypes.func.isRequired,
  deviceId: PropTypes.number.isRequired,
};

export { DownloadDataModal };
export default connectModalContainerContextToComponent(DownloadDataModal);
